import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalActionsComponent, PasslyCheckbox } from '@app/components';

import { useTypedTranslation } from '@app/hooks';
import { getModalSwitch, getLoaderBtn } from '@app/store/actions';
import { rolesService } from '@app/services';
import { AxiosResponse } from 'axios';
import { Box, CircularProgress } from '@material-ui/core';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';

interface IModalProps {
    handleRefresh: (skipCount: number, isReset: boolean) => void;
}
interface IRowProps {
    name: string;
    checked: boolean;
}

export const AddOrganizationRolesDialog: React.SFC<IModalProps> = ({ handleRefresh }) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const currentOrgId = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const [selectAllCheckbox, setSelectAllCheckbox] = useState(false);

    const [rolesForOrganization, setRolesForOrganization] = useState<IRowProps[]>([]);
    const [dataLoading, isDataLoading] = useState<boolean>(false);
    const isButtonDisabled = !rolesForOrganization.filter((el: IRowProps) => {
        return el.checked;
    }).length;

    useEffect(() => {
        handleGetRolesForOrganization();
    }, []);

    const handleGetRolesForOrganization = () => {
        isDataLoading(true);

        rolesService.getRolesForOrganization(currentOrgId).then((response: AxiosResponse) => {
            const roles = response.data.value;

            if (roles.length) {
                let newRows = roles.map((role: any) => {
                    const data: IRowProps = {
                        checked: false,
                        name: role
                    };
                    return data;
                });

                setRolesForOrganization(newRows);
            } else {
                setRolesForOrganization([]);
            }

            isDataLoading(false);
        });
    };

    const handleAddRoles = () => {
        const selectedItems = rolesForOrganization
            .filter((el: any) => {
                return el.checked === true;
            })
            .map((item) => item.name);

        dispatch(getLoaderBtn(true));
        rolesService
            .addRolesForOrganization(selectedItems, currentOrgId)
            .then(() => {
                handleRefresh(0, true);
                dispatch(getLoaderBtn(false));
                dispatch(getModalSwitch(false));
            })
            .catch(() => {
                dispatch(getLoaderBtn(false));
                dispatch(handleSetSnackbar({ show: true, message: translation('ADD_ORGANIZATION_ROLE_ERROR'), severity: 'error' }));
            });
    };

    const handleCancel = () => {
        dispatch(getModalSwitch(false));
    };

    const onCheckboxChange = (checked: boolean, e: any) => {
        const indexOfItem: number = rolesForOrganization.findIndex((item: IRowProps) => item.name === e.target.name);

        if (indexOfItem !== -1) {
            let newRows: IRowProps[] = [...rolesForOrganization];

            newRows[indexOfItem].checked = checked;
            setRolesForOrganization(newRows);
        }
    };

    const updateAllCheckboxes = (checked: boolean, e: any) => {
        if (rolesForOrganization.length > 0) {
            let newRows: IRowProps[] = [...rolesForOrganization];

            setSelectAllCheckbox(checked);

            if (e.target) {
                newRows.forEach((newRow) => {
                    newRow.checked = checked;
                });

                setRolesForOrganization(newRows);
            }
        }
    };

    return (
        <>
            <div tabIndex={0} className='modal__identities-list'>
                <div className='modal__identities-list--filter modal__identities-list--filter-no-padding-top'>
                    <PasslyCheckbox
                        name='selectall'
                        checked={selectAllCheckbox}
                        onChange={updateAllCheckboxes}
                        label={translation('SELECT_ALL')}
                    />
                </div>
                <div className='modal__identities-list--data modal__identities-list--data-small'>
                    {rolesForOrganization.length ? (
                        rolesForOrganization?.map((item: any) => (
                            <div className='modal__identities-list--row' key={item.name}>
                                <PasslyCheckbox
                                    name={item.name}
                                    checked={item.checked}
                                    onChange={onCheckboxChange}
                                    label=''
                                />
                                <Box ml={1}>{item.name}</Box>
                            </div>
                        ))
                    ) : dataLoading ? (
                            <CircularProgress size={64} className='modal__identities-list-loader' />
                    ) : (
                        <p className='modal__identities-list--no-data'>{translation('NO_MEMBER_ROLES_TO_ADD')}</p>
                    )}
                </div>
            </div>
            <ModalActionsComponent
                primaryBtn={translation('ADD_ROLES')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleAddRoles}
                onClickSecondary={handleCancel}
                disabled={isButtonDisabled}
                id='addRoles'
            />
        </>
    );
};
